import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserDarkMode } from 'store/Settings/actions';

const THEME_MODE_STORAGE_KEY = 'dcentriq_theme_mode';

const ThemeModeContext = createContext({
  mode: 'light',
  isDarkMode: false,
  setMode: () => {},
  toggleMode: () => {},
});

const getInitialMode = () => {
  try {
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch (error) {
    return 'light';
  }
};

export function ThemeModeProvider({ children }) {
  const dispatch = useDispatch();
  const persistedDarkMode = useSelector((state) => state.settings?.notifications?.darkMode);
  const [mode, setModeState] = useState(getInitialMode);

  React.useEffect(() => {
    if (typeof persistedDarkMode !== 'boolean') {
      return;
    }

    const nextMode = persistedDarkMode ? 'dark' : 'light';
    setModeState(nextMode);

    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, nextMode);
    } catch (error) {
      // no-op for environments where localStorage is unavailable
    }
  }, [persistedDarkMode]);

  const setMode = useCallback(
    (nextMode, options = {}) => {
      const { persist = true } = options;
      const normalized = nextMode === 'dark' ? 'dark' : 'light';
      setModeState(normalized);
      try {
        localStorage.setItem(THEME_MODE_STORAGE_KEY, normalized);
      } catch (error) {
        // no-op for environments where localStorage is unavailable
      }

      if (persist) {
        dispatch(updateUserDarkMode(normalized === 'dark'));
      }
    },
    [dispatch],
  );

  const toggleMode = useCallback(
    (nextDarkMode) => {
      let resolvedMode;
      if (typeof nextDarkMode === 'boolean') {
        resolvedMode = nextDarkMode ? 'dark' : 'light';
      } else {
        resolvedMode = mode === 'dark' ? 'light' : 'dark';
      }
      setMode(resolvedMode);
    },
    [mode, setMode],
  );

  const value = useMemo(
    () => ({
      mode,
      isDarkMode: mode === 'dark',
      setMode,
      toggleMode,
    }),
    [mode, setMode, toggleMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

ThemeModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeMode = () => useContext(ThemeModeContext);
